// components/personalize/AIPersonalizeModals.tsx
'use client';
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AIDedications from './AIDedications';
import CoversSelection from './CoversSelection';
import PackagingSelection from './PackagingSelection';
import Button from '@/components/Button';
import { CloseIcon } from '@/utils/svgIcons';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

const DesktopModal = ({ children }: { children: React.ReactNode }) => (
  <AnimatePresence>
    <motion.div 
      key="confirmation-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="hidden absolute inset-0 bg-black/40 z-[150] lg:flex items-center justify-center"
    >
      <div className="bg-white rounded-lg max-w-sm w-full text-center shadow-lg lg:min-w-[700px] max-h-[97vh] overflow-y-auto scrollbar-hide py-10 px-4">
        {children}
      </div>
    </motion.div>
  </AnimatePresence>
);

const NameModalContent = ({
  inputValues,
  UpdateValue,
  setChildName,
  setInputAge,
  is_edit,
  inputs,
  setNameModal,
  updateProductData,
  handleSave,
  childName,
  inputAge,
  childgender,
  isAIStory,
  croppedImage,
  setCroppedImage,
  imageSelected,
  setImageSelected,
  croppedFile,
  setCroppedFile,
  setAiStoryImage,
  setAiStoryFile,
  dedicationImageMode,
  setDedicationImageMode
}: any) => {
  const { t } = useTranslation('personalizeModal');
  const { locale } = useRouter();

  return (
    <div className='lg:max-w-full' dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>

      <div className='hidden lg:flex w-full items-center justify-between lg:mb-3'>
        <button 
          className="bg-[#93A3B03D] text-xl p-2 rounded-md" 
          onClick={() => setNameModal(false)}
        >
          <CloseIcon/>
        </button>
      </div>

      <div className="inputs flex flex-col gap-4 items-center mt-6">
        {Object.entries(inputValues).map(([key, value]) => {
          const isEmpty = value === null || value === undefined || 
                        (typeof value === 'string' && value.trim() === '');

          if (isEmpty && is_edit === '1') {
            return null;
          }

          return (
            <div key={key} className="w-full form-group">
              <label className={`${locale === 'ar' ? 'text-right' : 'text-left'} block text-xs`}>
                {key === 'name' && t('infoStepEnterName')}
                {key === 'first_name' && t('infoStepEnterFirstName')}
                {key === 'age' && t('infoStepEnterAge')}
                {key === 'init' && t('infoStepEnterInitial')}
              </label>
              <input
                type={key === 'age' ? 'number' : 'text'}
                name={key}
                min="1"
                max="100"
                onChange={e => {
                  const val = e.target.value;
                  UpdateValue(key, val);
                  if (key === 'name') setChildName(val);
                  if (key === 'age') setInputAge(val);
                }}
                value={key === 'age' ? inputAge : inputValues[key] ? inputValues[key] : ''}
                dir={locale === 'ar' ? 'rtl' : 'ltr'}
                className="border-1 text-primary border-gray-300 w-full font-semibold placeholder:font-light mt-2 placeholder:opacity-70 py-3 text-xs px-3 focus:ring-1 transition duration-200 focus:ring-black focus:border-transparent rounded-md"
              />
            </div>
          );
        })}
      </div>

      <Button
        type="button"
        bg="primary"
        onClick={() => {
          updateProductData({ name: childName, age: inputAge});
          handleSave();
          setNameModal(false);
        }}
        classes={['py-3', 'w-full', 'mt-4', 'font-semibold', 'hidden', 'lg:block', 'lg:mt-6']}>
        {`${t('aiStoryGenerateButton')}`}
      </Button>
    </div>
  );
};

const AIPersonalizeModals = ({
  dedicationModal,
  packageModal,
  coverModal,
  nameModal,
  isSettingsLoading,
  productData,
  inputPackaging,
  setInputPackaging,
  is_edit,
  setPackageModal,
  updateProductData,
  handleSave,
  settings,
  inputCover,
  setInputCover,
  setCoverModal,
  markOptionCompleted,
  setInputDedication,
  inputDedication,
  setOption,
  childgender,
  product,
  setDedicationModal,
  nameFromPersonalize,
  croppedImage,
  setCroppedImage,
  imageSelected,
  setImageSelected,
  updateDedicationImage,
  croppedFile,
  setCroppedFile,
  imageInEdit,
  inputValues,
  UpdateValue,
  setChildName,
  setInputAge,
  inputs,
  setNameModal,
  childName,
  inputAge,
  userCurrency,
  isAIStory,
  aiStoryImage,
  setAiStoryImage,
  setAiStoryFile,
  dedicationImageMode,
  setDedicationImageMode
}: any) => {
  const { locale } = useRouter();
  const { t } = useTranslation('personalizeModal');

  return (
    <>
      {dedicationModal && (
        <DesktopModal>
          <AIDedications
            setInputDedication={setInputDedication}
            inputDedication={inputDedication}
            setOption={setOption}
            gender={childgender}
            product={product}
            setDedicationModal={setDedicationModal}
            updateProductData={updateProductData}
            handleSave={handleSave}
            isEdit={is_edit}
            markOptionCompleted={markOptionCompleted}
            nameFromPersonalize={nameFromPersonalize}
            croppedImage={croppedImage}
            setCroppedImage={setCroppedImage}
            imageSelected={imageSelected}
            setImageSelected={setImageSelected}
            updateDedicationImage={updateDedicationImage}
            croppedFile={croppedFile}
            setCroppedFile={setCroppedFile}
            imageInEdit={imageInEdit}
            isAIStory={isAIStory}
            dedicationImageMode={dedicationImageMode}
            setDedicationImageMode={setDedicationImageMode}
            aiStoryImage={aiStoryImage}
          />
        </DesktopModal>
      )}
      
      {packageModal && (
        <DesktopModal>
          {!isSettingsLoading ? (
            <PackagingSelection
              product={productData?.product}
              inputPackaging={inputPackaging}
              setInputPackaging={setInputPackaging}
              isEdit={is_edit}
              setPackageModal={setPackageModal}
              updateProductData={updateProductData}
              handleSave={handleSave}
              packagingPrice={settings?.packaging_price}
              markCompleted={markOptionCompleted}
            />
          ) : (
            <p>{t('packagingLoadingText')}</p>
          )}
        </DesktopModal>
      )}

      {coverModal && (
        <DesktopModal>
          <CoversSelection
            product={productData?.product}
            inputCover={typeof inputCover === 'object' && inputCover !== null ? inputCover.name : inputCover}
            setInputCover={setInputCover}
            isEdit={is_edit}
            setCoverModal={setCoverModal}
            updateProductData={updateProductData}
            handleSave={handleSave}
            markOptionCompleted={markOptionCompleted}
            userCurrency={userCurrency}
          />
        </DesktopModal>
      )}

      {nameModal && (
        <DesktopModal>
          <NameModalContent
            inputValues={inputValues}
            UpdateValue={UpdateValue}
            setChildName={setChildName}
            setInputAge={setInputAge}
            is_edit={is_edit}
            inputs={inputs}
            setNameModal={setNameModal}
            updateProductData={updateProductData}
            handleSave={handleSave}
            childName={childName}
            inputAge={inputAge}
            childgender={childgender}
            isAIStory={isAIStory}
            croppedImage={croppedImage}
            setCroppedImage={setAiStoryImage}
            imageSelected={imageSelected}
            setImageSelected={setImageSelected}
            croppedFile={croppedFile}
            setCroppedFile={setAiStoryFile}
            setAiStoryImage={setAiStoryImage}
            setAiStoryFile={setAiStoryFile}
            dedicationImageMode={dedicationImageMode}
            setDedicationImageMode={setDedicationImageMode}
          />
        </DesktopModal>
      )}
    </>
  );
};

export default AIPersonalizeModals;